import React from "react";
import { Badge, Button, Callout, Card, DataTable, DisabledReason, EmptyState, Input, PageHeader } from "../components/DesignSystem";
import type { HouseholdInviteToken, ID, Invitation, WeddingWebsite } from "../domain/types";
import { personalizedWebsiteUrl } from "../domain/routing";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, LoadingState } from "./common";

function publicLink(token: HouseholdInviteToken): string {
  const search = typeof window !== "undefined" ? window.location.search : "";
  const suffix = search ? `${search}` : "";
  return `${window.location.origin}${token.link || `/guest-rsvp/${token.token}`}${suffix}`;
}

/** Only shown once a token exists and a website is actually published. */
function personalizedWebsiteLink(website: WeddingWebsite, token: HouseholdInviteToken): string {
  return personalizedWebsiteUrl(window.location.origin, website.slug, token.token);
}

function absoluteInvitationLink(link: string): string {
  const search = typeof window !== "undefined" ? window.location.search : "";
  const suffix = link.includes("?") || !search ? "" : search;
  if (link.startsWith("http://") || link.startsWith("https://")) return `${link}${suffix}`;
  return `${window.location.origin}${link.startsWith("/") ? link : `/${link}`}${suffix}`;
}

export function InvitationsScreen() {
  const { workspace, loading, error, service, run } = usePlannerData();
  const [links, setLinks] = React.useState<Record<ID, HouseholdInviteToken>>({});
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [creatingFor, setCreatingFor] = React.useState<ID | null>(null);
  const [website, setWebsite] = React.useState<WeddingWebsite | null>(null);

  React.useEffect(() => {
    service.website.get().then(setWebsite).catch(() => setWebsite(null));
  }, [service]);

  if (loading || !workspace) return <div className="pob-pad"><LoadingState label="Loading invitations" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;
  const publishedWebsite = website && website.status === "published" ? website : null;

  const createLink = async (invitation: Invitation) => {
    setActionError(null);
    setCreatingFor(invitation.householdId);
    try {
      const token = await run(() => service.invitations.createHouseholdInviteToken(invitation.householdId), { refresh: false });
      setLinks((current) => ({ ...current, [invitation.householdId]: token }));
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setCreatingFor(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Guests"
        title="Invitations"
        meta={[{ icon: "mail", label: `${workspace.invitations.length} households` }, { icon: "shield-check", label: "Token-scoped RSVP links" }]}
        actions={<><DisabledReason reason="Mailing-list export is waiting for the reporting/export API."><Button icon="download" disabled>Export mailing list</Button></DisabledReason><DisabledReason reason="Invitation dispatch tracking is waiting for the messaging API."><Button variant="primary" icon="send" disabled>Record dispatch</Button></DisabledReason></>}
      />
      <div className="pob-pad">
        {actionError && <Callout tone="error" title="Invitation action failed">{actionError}</Callout>}
        <Callout tone="neutral" title="Guest links are scoped by household">
          Opening a generated RSVP link returns only that household's invited events from the backend.
        </Callout>
        {workspace.invitations.length === 0 ? (
          <Card><EmptyState icon="mail" title="No invitations yet" description="Add a household and guests before creating RSVP links." /></Card>
        ) : (
          <Card padding="0">
            <DataTable rows={workspace.invitations} columns={[
              { key: "household", header: "Household", render: (invitation) => workspace.households.find((household) => household.id === invitation.householdId)?.name || invitation.householdId },
              { key: "events", header: "Invited to", muted: true, wrap: true, render: (invitation) => invitation.eventIds.map((eventId) => workspace.events.find((event) => event.id === eventId)?.name || eventId).join(", ") || "No celebrations yet" },
              { key: "status", header: "Status", render: (invitation) => <Badge tone={links[invitation.householdId] || invitation.link ? "success" : "neutral"}>{links[invitation.householdId] || invitation.link ? "Link ready" : invitation.status.replace("_", " ")}</Badge> },
              { key: "link", header: "RSVP link", width: 360, render: (invitation) => {
                const link = links[invitation.householdId] ? publicLink(links[invitation.householdId]) : invitation.link ? absoluteInvitationLink(invitation.link) : "";
                return link ? <Input size="sm" readOnly value={link} /> : <span style={{ color: "var(--text-tertiary)" }}>Not generated</span>;
              } },
              ...(publishedWebsite ? [{ key: "website", header: "Personalized website link", width: 360, render: (invitation: Invitation) => {
                const token = links[invitation.householdId];
                return token ? <Input size="sm" readOnly value={personalizedWebsiteLink(publishedWebsite, token)} /> : <span style={{ color: "var(--text-tertiary)" }}>Create an RSVP link first</span>;
              } }] : []),
              { key: "actions", header: "", width: 210, render: (invitation) => {
                const token = links[invitation.householdId];
                const link = token ? publicLink(token) : invitation.link ? absoluteInvitationLink(invitation.link) : "";
                return link ? (
                  <Button size="sm" variant="primary" onClick={() => { window.location.href = link; }}>Open RSVP</Button>
                ) : (
                  <Button size="sm" disabled={creatingFor === invitation.householdId} onClick={() => createLink(invitation)}>{creatingFor === invitation.householdId ? "Creating..." : "Create link"}</Button>
                );
              } }
            ]} />
          </Card>
        )}
      </div>
    </div>
  );
}
